import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SecureDocViewer from "@/components/SecureDocViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { BookOpen, FileText, Scale, FlaskConical, Plus, Lock, ChevronRight, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "playbook", label: "Playbooks", icon: BookOpen },
  { key: "clause", label: "Clause Library", icon: Scale },
  { key: "research", label: "Research Memos", icon: FlaskConical },
];

export default function PracticeGroups() {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("playbook");
  const [articles, setArticles] = useState([]);
  const [docs, setDocs] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", tags: "" });
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: "", description: "" });
  const [groupEditId, setGroupEditId] = useState(null);
  const [aq, setAq] = useState("");

  const loadGroups = useCallback((selectId) => api.get("/practice-groups").then((r) => {
    setGroups(r.data);
    if (selectId) { const g = r.data.find((x) => x.id === selectId); if (g) setActive(g); }
    else setActive((cur) => cur || r.data[0] || null);
  }).catch((e) => console.error("practice groups load", e)), []);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const loadContent = useCallback(() => {
    if (!active) return;
    api.get("/articles-filter", { params: { section: tab, practice_group: active.name } }).then((r) => setArticles(r.data)).catch((e) => console.error("articles load", e));
    if (tab === "playbook") api.get("/documents", { params: { practice_group: active.name, category: "brief-bank" } }).then((r) => setDocs(r.data)).catch((e) => console.error("brief bank load", e));
    else setDocs([]);
  }, [active, tab]);
  useEffect(() => { loadContent(); }, [loadContent]);

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    try {
      await api.post("/articles", { section: tab, practice_group: active.name, title: form.title, body: form.body, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Saved"); setOpen(false); setForm({ title: "", body: "", tags: "" }); loadContent();
    } catch { toast.error("Could not save (editor access required)"); }
  };
  const removeArticle = async (id) => { try { await api.delete(`/articles/${id}`); toast.success("Removed"); loadContent(); } catch { toast.error("Could not remove"); } };

  const openNewGroup = () => { setGroupEditId(null); setGroupForm({ name: "", description: "" }); setGroupOpen(true); };
  const openEditGroup = () => { if (!active) return; setGroupEditId(active.id); setGroupForm({ name: active.name, description: active.description || "" }); setGroupOpen(true); };
  const saveGroup = async () => {
    if (!groupForm.name) return toast.error("Name required");
    try {
      if (groupEditId) { const r = await api.put(`/practice-groups/${groupEditId}`, groupForm); toast.success("Group updated"); setGroupOpen(false); loadGroups(groupEditId); setActive(r.data); }
      else { const r = await api.post("/practice-groups", groupForm); toast.success("Group added"); setGroupOpen(false); loadGroups(r.data.id); }
    } catch { toast.error("Could not save (editor access required)"); }
  };
  const removeGroup = async () => {
    if (!active) return;
    try { await api.delete(`/practice-groups/${active.id}`); toast.success("Group removed"); setActive(null); loadGroups(); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div data-testid="practice-page">
      <PageHeader overline="Practice Spokes" title="Practice Groups" subtitle="Group playbooks, brief bank, clause libraries and research memos — tagged by issue and jurisdiction.">
        {editor && <Button variant="outline" data-testid="add-group-btn" onClick={openNewGroup}><Plus className="h-4 w-4 mr-1.5" /> Add Group</Button>}
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="group-selector">
        {groups.map((g) => (
          <button key={g.id} data-testid="group-pill" onClick={() => setActive(g)}
            className={`px-3.5 py-2 text-sm rounded-sm border transition-colors ${active?.id === g.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
            {g.name}
          </button>
        ))}
      </div>

      {active && (
        <>
          <div className="border border-border bg-card rounded-sm p-5 mb-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display font-bold text-lg">{active.name}</h2>
                <p className="text-sm text-muted-foreground mt-1">{active.description}</p>
              </div>
              {editor && (
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid="edit-group-btn" onClick={openEditGroup} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                  <button data-testid="delete-group-btn" onClick={removeGroup} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-border mb-5">
            <div className="flex gap-1">
              {TABS.map((t) => (
                <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === t.key ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  <t.icon className="h-4 w-4" /> {t.label}
                </button>
              ))}
            </div>
            {editor && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-article-btn"><Plus className="h-4 w-4 mr-1.5" /> Add {TABS.find((t) => t.key === tab)?.label.replace(/s$/, "")}</Button></DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>New entry — {active.name}</DialogTitle><DialogDescription className="sr-only">Add practice group content.</DialogDescription></DialogHeader>
                  <div className="space-y-3">
                    <div><Label className="text-xs">Title</Label><Input data-testid="article-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                    <div><Label className="text-xs">Body</Label><Textarea data-testid="article-body" rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
                    <div><Label className="text-xs">Tags (comma separated)</Label><Input data-testid="article-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button data-testid="save-article-btn" onClick={save}>Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {articles.length > 0 && (
            <div className="relative mb-4 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="article-search" value={aq} onChange={(e) => setAq(e.target.value)} placeholder="Search this section…" className="pl-9" />
            </div>
          )}

          {tab === "playbook" && docs.length > 0 && (
            <div className="mb-6">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Brief Bank — {active.name}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {docs.map((d) => (
                  <button key={d.id} data-testid="brief-item" onClick={() => setViewer(d)} className="text-left border border-border bg-card p-4 rounded-sm hover:border-primary transition-colors">
                    <div className="flex items-center gap-2 text-xs text-primary mb-1"><Lock className="h-3 w-3" /> Secure Precedent</div>
                    <div className="text-sm font-semibold">{d.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{d.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {articles.length === 0 && docs.length === 0 ? (
            <EmptyState icon={BookOpen} title="Nothing here yet" hint={editor ? "Use the Add button to create the first entry." : "Content will appear as attorneys add it."} />
          ) : (
            <div className="space-y-2" data-testid="article-list">
              {articles.filter((a) => !aq || `${a.title} ${(a.tags || []).join(" ")} ${a.body || ""}`.toLowerCase().includes(aq.toLowerCase())).map((a) => (
                <div key={a.id} data-testid="article-item" className="w-full flex items-center gap-3 border border-border bg-card p-4 rounded-sm hover:border-primary transition-colors">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  <button onClick={() => setViewer({ ...a, description: a.tags?.join(" · ") })} className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-semibold">{a.title}</div>
                    {a.tags?.length > 0 && <div className="text-xs text-muted-foreground truncate">{a.tags.join(" · ")}</div>}
                  </button>
                  {editor && <button data-testid="delete-article-btn" onClick={() => removeArticle(a.id)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>}
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
        <DialogContent className="max-w-md" data-testid="group-dialog">
          <DialogHeader><DialogTitle>{groupEditId ? "Edit Practice Group" : "Add Practice Group"}</DialogTitle><DialogDescription className="sr-only">Practice group form.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input data-testid="group-name" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} /></div>
            <div><Label className="text-xs">Description</Label><Textarea data-testid="group-description" rows={3} value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="save-group-btn" onClick={saveGroup}>{groupEditId ? "Save Changes" : "Add Group"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <SecureDocViewer doc={viewer} open={!!viewer} onOpenChange={(o) => !o && setViewer(null)} viewerName={user?.name} />
    </div>
  );
}
